import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import {
  ComponentMultiStyleConfig,
  Th,
  Thead,
  Tr,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { SearchResultHeaderContent } from "../../row/types";
import { hasValue, isNullish } from "../../../../lib/util/common-util";
import { LayoutProps, PositionProps } from "@chakra-ui/styled-system";

export type CMSearchResultHeaderProps = {
  content: SearchResultHeaderContent;

  /**
   * この値を指定すると、ヘッダ行がstickyになる。
   * この値は、その際のtopの値となる。
   */
  stickyHeaderTop?: PositionProps["top"];
  /**
   * この値を指定すると、1列目がstickyになる。
   * この値は、その際のleftの値となる。
   * 注意: カテゴリありのヘッダ(2行に亘るヘッダ)を固定列とするのは未サポート。
   */
  stickyFirstColLeft?: PositionProps["left"];
  /**
   * この値を指定すると、2列目がstickyになる。
   * この値は、その際のleftの値となる。
   * 注意: カテゴリありのヘッダ(2行に亘るヘッダ)を固定列とするのは未サポート。
   */
  stickySecondColLeft?: PositionProps["left"];
} & ComponentStyleProps;

export const CMSearchResultHeader = ({
  content,

  stickyHeaderTop,
  stickyFirstColLeft,
  stickySecondColLeft,

  sx,
  ...rest
}: CMSearchResultHeaderProps) => {
  const styles = useMultiStyleConfig("CMSearchResultHeaderStyles");

  const stickyHeaderStyle = useMemo(() => {
    if (isNullish(stickyHeaderTop)) return undefined;
    return {
      ...styles.stickyHeader,
      top: stickyHeaderTop,
    };
  }, [stickyHeaderTop, styles.stickyHeader]);
  const stickyFirstColStyle = useMemo(() => {
    if (isNullish(stickyFirstColLeft)) return undefined;
    return {
      ...styles.stickyFirstCol,
      left: stickyFirstColLeft,
    };
  }, [stickyFirstColLeft, styles]);
  const stickySecondColStyle = useMemo(() => {
    if (isNullish(stickySecondColLeft)) return undefined;
    return {
      ...styles.stickySecondCol,
      left: stickySecondColLeft,
    };
  }, [stickySecondColLeft, styles]);

  const row0 = useMemo(() => {
    const cells = content.cells;
    let row0Cells: {
      value: string;
      rowSpan: number;
      colSpan: number;
      width?: LayoutProps["width"];
    }[] = [];
    let currentColSpan = 0;
    cells.forEach((c, idx) => {
      if (isNullish(c.category)) {
        row0Cells = [
          ...row0Cells,
          { value: c.value, rowSpan: 2, colSpan: 1, width: c.width },
        ];
      } else {
        const currentCategory = c.category;
        currentColSpan += 1;
        const nextIdx = idx + 1;
        const isLastOfCurrentCategory = (() => {
          if (cells.length <= nextIdx) return true;
          const nextCell = cells[nextIdx];
          if (isNullish(nextCell.category)) return true;
          if (nextCell.category !== currentCategory) return true;
          return false;
        })();
        if (isLastOfCurrentCategory) {
          row0Cells = [
            ...row0Cells,
            { value: currentCategory, rowSpan: 1, colSpan: currentColSpan },
          ];
          currentColSpan = 0;
        }
      }
    });
    return row0Cells;
  }, [content]);

  const row1 = useMemo(() => {
    const cells = content.cells;
    return cells
      .filter((c) => hasValue(c.category))
      .map((c) => ({
        value: c.value,
        rowSpan: 1,
        colSpan: 1,
        width: c.width,
      }));
  }, [content]);

  return (
    <Thead {...rest} sx={stickyHeaderStyle}>
      <Tr sx={{ ...styles.container, ...sx }}>
        {row0.map((c, idx) => (
          <Th
            key={idx}
            rowSpan={c.rowSpan}
            colSpan={c.colSpan}
            minW={c.width}
            w={c.width}
            textTransform={"none"}
            maxW={
              (idx === 0 && hasValue(stickyFirstColLeft)) ||
              (idx === 1 && hasValue(stickySecondColLeft))
                ? c.width
                : undefined
            }
            sx={{
              ...styles.cell,
              ...(idx === 0 ? stickyFirstColStyle : undefined),
              ...(idx === 1 ? stickySecondColStyle : undefined),
            }}
          >
            {c.value}
          </Th>
        ))}
      </Tr>
      <Tr sx={{ ...styles.container, ...sx }}>
        {row1.map((c, idx) => (
          <Th
            key={idx}
            rowSpan={c.rowSpan}
            colSpan={c.colSpan}
            sx={styles.cell}
            minW={c.width}
            w={c.width}
            maxW={c.width}
            textTransform={"none"}
          >
            {c.value}
          </Th>
        ))}
      </Tr>
    </Thead>
  );
};
export const CMSearchResultHeaderStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "cell",
    "stickyHeader",
    "stickyFirstCol",
    "stickySecondCol",
  ],
  baseStyle: {
    container: {
      border: "0",
    },
    cell: {
      pl: "10px",
      pr: "8px",
      py: "8px",

      bgColor: "gray.100",

      whiteSpace: "normal",
      overflow: "hidden",

      fontSize: "xs",
      color: "gray.900",

      borderRightWidth: "1px",
      borderColor: "gray.300",
      _last: {
        borderRightStyle: "none",
      },
    },
    stickyHeader: {
      position: "sticky",
      zIndex: 2,
      // topはpropsから与える。
      "::before": {
        content: '""',
        position: "absolute",
        top: "-1px",
        left: "0px",
        width: "100%",
        height: "100%",
        borderTopWidth: "1px",
        borderColor: "gray.400",
        zIndex: "-1",
      },
    },
    stickyFirstCol: {
      position: "sticky",
      zIndex: 1,
      // leftはpropsから与える。
      "::before": {
        content: '""',
        position: "absolute",
        top: "0px",
        left: "0px",
        width: "100%",
        height: "100%",
        borderLeftWidth: "1px",
        borderLeftColor: "gray.400",
        // そのまま重ねるとリンク要素が有効にならないため、z-indexで調整
        zIndex: "-1",
      },
    },
    stickySecondCol: {
      position: "sticky",
      zIndex: 1,
      // leftはpropsから与える。
      "::before": {
        content: '""',
        position: "absolute",
        top: "0px",
        left: "0px",
        width: "100%",
        height: "100%",
        borderLeftWidth: "1px",
        borderLeftColor: "gray.200",
        // そのまま重ねるとリンク要素が有効にならないため、z-indexで調整
        zIndex: "-1",
      },
    },
  },
};
